import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const GenerationProgress = ({ 
  isVisible = false, 
  onComplete, 
  onCancel,
  plannerData,
  className = ""
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [estimatedTime, setEstimatedTime] = useState(60);

  const generationSteps = [
    {
      id: 1,
      title: "Initializing",
      description: "Setting up planner structure...",
      duration: 1000
    },
    {
      id: 2,
      title: "Processing Layout",
      description: "Applying your custom layout and design...",
      duration: 1000
    },
    {
      id: 3,
      title: "Generating Pages",
      description: "Creating calendar pages and sections...",
      duration: 1000
    },
    {
      id: 4,
      title: "Adding Content",
      description: "Inserting dates, holidays, and custom elements...",
      duration: 1000
    },
    {
      id: 5,
      title: "Optimizing PDF",
      description: "Compressing and finalizing your planner...",
      duration: 1000
    },
    {
      id: 6,
      title: "Complete",
      description: "Your planner is ready for download!",
      duration: 50
    }
  ];

  useEffect(() => {
    if (!isVisible) {
      setCurrentStep(0);
      setProgress(0);
      setEstimatedTime(60);
      return;
    }

    let stepIndex = 0;
    let progressValue = 0;
    const totalDuration = generationSteps?.reduce((sum, step) => sum + step?.duration, 0);

    const processStep = () => {
      if (stepIndex >= generationSteps?.length) {
        onComplete?.();
        return;
      }

      setCurrentStep(stepIndex);
      const step = generationSteps?.[stepIndex];
      const stepProgress = (stepIndex / generationSteps?.length) * 100;
      
      // Animate progress within the step
      const progressInterval = setInterval(() => {
        progressValue += (100 / generationSteps?.length) / (step?.duration / 100);
        setProgress(Math.min(progressValue, stepProgress + (100 / generationSteps?.length)));
        
        // Update estimated time
        const elapsed = (stepIndex * (totalDuration / generationSteps?.length)) + 
                       ((progressValue - stepProgress) / (100 / generationSteps?.length)) * step?.duration;
        const remaining = Math.max(0, Math.ceil((totalDuration - elapsed) / 1000));
        setEstimatedTime(remaining);
      }, 100);

      setTimeout(() => {
        clearInterval(progressInterval);
        stepIndex++;
        processStep();
      }, step?.duration);
    };

    processStep();
  }, [isVisible]);

  if (!isVisible) return null;

  const currentStepData = generationSteps?.[currentStep];
  const isComplete = currentStep >= generationSteps?.length - 1;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`bg-card border border-border rounded-lg max-w-md w-full ${className}`}>
        {/* Header */}
        <div className="p-6 border-b border-border text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            {isComplete ? (
              <Icon name="Check" size={32} className="text-success" />
            ) : (
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            )}
          </div>
          
          <h2 className="font-heading text-xl font-semibold text-foreground mb-2">
            {isComplete ? 'Generation Complete!' : 'Generating Your Planner'}
          </h2>
          
          <p className="font-body text-sm text-muted-foreground">
            {isComplete 
              ? 'Your planner has been successfully created and is ready for download.'
              : 'Please wait while we create your personalized planner. This may take a few moments.'
            }
          </p>
        </div>

        {/* Progress Content */}
        <div className="p-6">
          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="font-body text-sm text-muted-foreground">
                Progress
              </span>
              <span className="font-body text-sm font-medium text-foreground">
                {Math.round(progress)}%
              </span>
            </div>
            
            <div className="w-full bg-muted rounded-full h-3">
              <div 
                className="bg-primary h-3 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Current Step */}
          {currentStepData && (
            <div className="mb-6">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <span className="font-heading text-sm font-semibold text-primary-foreground">
                    {currentStepData?.id}
                  </span>
                </div>
                <h3 className="font-heading font-semibold text-foreground">
                  {currentStepData?.title}
                </h3>
              </div>
              
              <p className="font-body text-sm text-muted-foreground ml-11">
                {currentStepData?.description}
              </p>
            </div>
          )}

          {/* Time Estimate */}
          {!isComplete && (
            <div className="bg-muted rounded-lg p-3 mb-6">
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={16} color="var(--color-muted-foreground)" />
                <span className="font-body text-sm text-muted-foreground">
                  Estimated time remaining: {estimatedTime} seconds
                </span>
              </div>
            </div>
          )}

          {/* Planner Info */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
            <h4 className="font-heading font-medium text-foreground mb-2">
              Generating: {plannerData?.coverTitle || "Planner"}
            </h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="font-body text-muted-foreground">
                Pages: ~365
              </div>
              <div className="font-body text-muted-foreground">
                Format: PDF
              </div>
              <div className="font-body text-muted-foreground">
                Size: ~15MB
              </div>
              <div className="font-body text-muted-foreground">
                Quality: High
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            {isComplete ? (
              <Button
                variant="default"
                fullWidth
                onClick={onComplete}
                iconName="Download"
                iconPosition="left"
              >
                Download Planner
              </Button>
            ) : (
              <Button
                variant="outline"
                fullWidth
                onClick={onCancel}
                iconName="X"
                iconPosition="left"
              >
                Cancel Generation
              </Button>
            )}
          </div>

          {/* Tips */}
          {!isComplete && (
            <div className="mt-4 p-3 bg-accent/10 border border-accent/20 rounded-lg">
              <div className="flex items-start space-x-2">
                <Icon name="Lightbulb" size={16} className="text-accent mt-0.5" />
                <div>
                  <p className="font-body text-sm text-foreground font-medium mb-1">
                    Did you know?
                  </p>
                  <p className="font-body text-xs text-muted-foreground">
                    Your planner will include interactive PDF bookmarks for easy navigation between sections.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GenerationProgress;